import React from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  HandHeart,
  MapPin,
  Rocket,
  ShieldCheck,
  Target,
  Users,
  Wallet,
  BarChart3,
  Smartphone,
  MessageCircle,
} from "lucide-react";

const goal = 25000;
const raised = 0;
const progress = Math.min((raised / goal) * 100, 100);

const contributionLevels = [
  {
    amount: "KES 500",
    title: "Support a Field Visit",
    description:
      "Helps cover local movement and communication needed to reach a potential pilot group.",
    icon: MapPin,
  },
  {
    amount: "KES 1,000",
    title: "Help Onboard a Group",
    description:
      "Supports outreach, product demonstrations and onboarding activities.",
    icon: Users,
  },
  {
    amount: "KES 2,500",
    title: "Reach Multiple Groups",
    description:
      "Helps us conduct clustered field engagement and introduce MastaKash to several groups.",
    icon: Target,
  },
  {
    amount: "KES 5,000",
    title: "Sponsor a Field Day",
    description:
      "Helps cover transportation, communication and field-support activities for a concentrated outreach day.",
    icon: Rocket,
  },
  {
    amount: "KES 10,000",
    title: "Pilot Launch Supporter",
    description:
      "Provides a significant contribution toward launching the initial MastaKash field deployment.",
    icon: HandHeart,
    featured: true,
  },
];

const budgetItems = [
  {
    label: "Field transport & local movement",
    amount: 10000,
    percentage: 40,
  },
  {
    label: "Group outreach & communication",
    amount: 3750,
    percentage: 15,
  },
  {
    label: "Demonstrations & onboarding materials",
    amount: 2500,
    percentage: 10,
  },
  {
    label: "Airtime & data",
    amount: 2500,
    percentage: 10,
  },
  {
    label: "User onboarding & field support",
    amount: 2500,
    percentage: 10,
  },
  {
    label: "Pilot documentation & data collection",
    amount: 1250,
    percentage: 5,
  },
  {
    label: "Contingency",
    amount: 2500,
    percentage: 10,
  },
];

const pilotOutcomes = [
  "10–15 community financial groups reached",
  "Initial groups onboarded onto MastaKash",
  "Real users supported through early adoption",
  "Actual usage and transaction evidence collected",
  "User feedback and adoption barriers documented",
  "Early case studies and testimonials developed",
];

function formatKES(amount) {
  return new Intl.NumberFormat("en-KE").format(amount);
}

export default function MastaKashPilotLandingPage() {
  const handleSupport = (amount = null) => {
    // Replace this with your payment/fundraising flow.
    console.log("Support amount:", amount);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* ============================================================
          NAVIGATION
      ============================================================ */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#24103f]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <a href="#" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500 text-white shadow-lg">
              <Wallet size={21} />
            </div>

            <div>
              <div className="text-lg font-bold tracking-tight text-white">
                MastaKash
              </div>
              <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-purple-200">
                Community Finance
              </div>
            </div>
          </a>

          <nav className="hidden items-center gap-8 md:flex">
            <a
              href="#pilot"
              className="text-sm font-medium text-purple-100 transition hover:text-white"
            >
              The Pilot
            </a>

            <a
              href="#impact"
              className="text-sm font-medium text-purple-100 transition hover:text-white"
            >
              What We Aim to Prove
            </a>

            <a
              href="#budget"
              className="text-sm font-medium text-purple-100 transition hover:text-white"
            >
              Where Funds Go
            </a>

            <button
              onClick={() => handleSupport()}
              className="rounded-full bg-orange-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-orange-600"
            >
              Support the Pilot
            </button>
          </nav>

          <button
            onClick={() => handleSupport()}
            className="rounded-full bg-orange-500 px-4 py-2 text-sm font-bold text-white md:hidden"
          >
            Support
          </button>
        </div>
      </header>

      {/* ============================================================
          HERO
      ============================================================ */}
      <section className="relative overflow-hidden bg-[#24103f]">
        <div className="absolute inset-0">
          <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-purple-700/20 blur-3xl" />
          <div className="absolute -right-20 bottom-0 h-96 w-96 rounded-full bg-orange-500/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-16 lg:px-8 lg:pb-28 lg:pt-24">
          <div className="grid items-center gap-14 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-purple-300/20 bg-white/10 px-4 py-2 text-sm font-semibold text-purple-100">
                <span className="h-2 w-2 rounded-full bg-orange-400" />
                MastaKash Pilot Launch Fund
              </div>

              <h1 className="max-w-4xl text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Help us put{" "}
                <span className="text-orange-400">MastaKash</span> into the
                hands of community financial groups.
              </h1>

              <p className="mt-7 max-w-2xl text-lg leading-8 text-purple-100 sm:text-xl">
                We are raising{" "}
                <strong className="text-white">KES 25,000</strong> to launch a
                focused 60-day field pilot across Trans Nzoia and Kakamega —
                reaching 10–15 community financial groups and generating the
                real-world evidence needed to take MastaKash to the next level.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={() => handleSupport()}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 font-bold text-white shadow-xl shadow-orange-950/20 transition hover:bg-orange-600"
                >
                  Support the Pilot
                  <ArrowRight
                    size={18}
                    className="transition group-hover:translate-x-1"
                  />
                </button>

                <a
                  href="#pilot"
                  className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 font-bold text-white transition hover:bg-white/10"
                >
                  See the Pilot Plan
                </a>
              </div>

              <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-purple-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={17} className="text-orange-400" />
                  60-day pilot
                </div>

                <div className="flex items-center gap-2">
                  <CheckCircle2 size={17} className="text-orange-400" />
                  10–15 groups targeted
                </div>

                <div className="flex items-center gap-2">
                  <CheckCircle2 size={17} className="text-orange-400" />
                  Two counties
                </div>
              </div>
            </div>

            {/* Fundraising Card */}
            <div className="rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-500">
                    Pilot Launch Fund
                  </p>
                  <p className="mt-1 text-3xl font-extrabold text-[#24103f]">
                    KES {formatKES(raised)}
                  </p>
                </div>

                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                  <CircleDollarSign size={28} />
                </div>
              </div>

              <div className="mt-7">
                <div className="mb-2 flex justify-between text-sm font-semibold">
                  <span className="text-slate-500">
                    {Math.round(progress)}% funded
                  </span>
                  <span className="text-[#24103f]">
                    KES {formatKES(goal)}
                  </span>
                </div>

                <div className="h-4 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-orange-500 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-purple-50 p-4">
                  <div className="text-2xl font-extrabold text-[#24103f]">
                    60
                  </div>
                  <div className="mt-1 text-xs font-medium text-slate-500">
                    Days
                  </div>
                </div>

                <div className="rounded-2xl bg-orange-50 p-4">
                  <div className="text-2xl font-extrabold text-[#24103f]">
                    10–15
                  </div>
                  <div className="mt-1 text-xs font-medium text-slate-500">
                    Groups targeted
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleSupport()}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#24103f] px-5 py-4 font-bold text-white transition hover:bg-[#321556]"
              >
                Help us reach the goal
                <ArrowRight size={18} />
              </button>

              <p className="mt-4 text-center text-xs leading-5 text-slate-400">
                Every contribution moves the pilot closer to the field.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          WHY THIS PILOT
      ============================================================ */}
      <section id="pilot" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-100 text-[#24103f]">
              <Rocket size={24} />
            </div>

            <p className="font-bold uppercase tracking-[0.18em] text-orange-500">
              From product to proof
            </p>

            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#24103f] sm:text-4xl">
              The next milestone is not more technology. It is real-world
              validation.
            </h2>

            <p className="mt-5 text-lg leading-8 text-slate-600">
              MastaKash has been developed to support community-based financial
              groups. Our next step is to put it into the hands of real groups,
              understand how they use it, learn what needs improvement and
              generate evidence for the next stage of growth.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {[
              {
                number: "01",
                title: "Reach",
                text: "Identify and engage community financial groups in Trans Nzoia and Kakamega.",
              },
              {
                number: "02",
                title: "Validate",
                text: "Onboard initial groups and observe how MastaKash performs in real-world use.",
              },
              {
                number: "03",
                title: "Learn",
                text: "Collect usage data, feedback and lessons that will guide product and operational decisions.",
              },
            ].map((item) => (
              <div
                key={item.number}
                className="rounded-3xl border border-slate-100 bg-slate-50 p-7"
              >
                <div className="text-sm font-extrabold text-orange-500">
                  {item.number}
                </div>

                <h3 className="mt-4 text-2xl font-extrabold text-[#24103f]">
                  {item.title}
                </h3>

                <p className="mt-3 leading-7 text-slate-600">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          THE PROBLEM
      ============================================================ */}
      <section className="bg-slate-50 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="font-bold uppercase tracking-[0.18em] text-orange-500">
                Why it matters
              </p>

              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#24103f] sm:text-4xl">
                Community finance is already working. The infrastructure needs
                to catch up.
              </h2>

              <p className="mt-6 text-lg leading-8 text-slate-600">
                Across Kenya, people save, invest and support one another
                through chamas, table-banking groups, merry-go-rounds, SACCOs,
                save-to-own arrangements and other community financial groups.
              </p>

              <p className="mt-5 leading-7 text-slate-600">
                Yet many groups still depend on manual records, fragmented
                processes and systems that make financial activity difficult to
                organize, monitor and build into reliable digital histories.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                "Manual record keeping",
                "Fragmented financial processes",
                "Difficult reconciliation",
                "Limited financial visibility",
                "Cash-heavy operations",
                "Limited digital infrastructure",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-2xl bg-white p-5 shadow-sm"
                >
                  <div className="mt-0.5 rounded-full bg-orange-50 p-1.5 text-orange-500">
                    <CheckCircle2 size={16} />
                  </div>

                  <span className="font-semibold text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          PILOT PLAN
      ============================================================ */}
      <section className="bg-[#24103f] py-20 text-white lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="font-bold uppercase tracking-[0.18em] text-orange-400">
                The 60-day pilot
              </p>

              <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
                A focused field deployment designed to generate evidence.
              </h2>

              <p className="mt-6 leading-8 text-purple-100">
                We will begin with targeted community outreach rather than
                attempting to scale prematurely. The objective is to learn
                quickly, support users closely and document what we discover.
              </p>

              <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4">
                <MapPin className="text-orange-400" size={21} />
                <div>
                  <div className="font-bold">Pilot geography</div>
                  <div className="text-sm text-purple-200">
                    Trans Nzoia & Kakamega Counties
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {[
                {
                  step: "01",
                  title: "Identify",
                  text: "Find and engage suitable chamas, saving circles, table-banking groups, SACCOs and other community financial groups.",
                },
                {
                  step: "02",
                  title: "Demonstrate",
                  text: "Introduce MastaKash, demonstrate the platform and understand each group's workflow and needs.",
                },
                {
                  step: "03",
                  title: "Onboard",
                  text: "Configure participating groups and support leaders and members as they begin using the platform.",
                },
                {
                  step: "04",
                  title: "Support",
                  text: "Provide practical field support while groups transition into digital financial management.",
                },
                {
                  step: "05",
                  title: "Measure",
                  text: "Track adoption, engagement, activity and feedback throughout the pilot.",
                },
                {
                  step: "06",
                  title: "Document",
                  text: "Capture evidence, testimonials, lessons and case studies to inform the next stage.",
                },
              ].map((item) => (
                <div
                  key={item.step}
                  className="flex gap-5 rounded-2xl border border-white/10 bg-white/5 p-5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-500 font-extrabold">
                    {item.step}
                  </div>

                  <div>
                    <h3 className="font-bold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-purple-100">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          CONTRIBUTIONS
      ============================================================ */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="font-bold uppercase tracking-[0.18em] text-orange-500">
              Every contribution matters
            </p>

            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#24103f] sm:text-4xl">
              You don't have to fund the whole pilot to help make it happen.
            </h2>

            <p className="mt-5 text-lg leading-8 text-slate-600">
              Contributions of different sizes can help us move faster toward
              the KES 25,000 launch target.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {contributionLevels.map((level) => {
              const Icon = level.icon;

              return (
                <div
                  key={level.amount}
                  className={`relative rounded-3xl border p-7 transition hover:-translate-y-1 hover:shadow-xl ${
                    level.featured
                      ? "border-orange-200 bg-orange-50"
                      : "border-slate-100 bg-slate-50"
                  }`}
                >
                  {level.featured && (
                    <div className="absolute right-5 top-5 rounded-full bg-orange-500 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white">
                      Featured
                    </div>
                  )}

                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#24103f] shadow-sm">
                    <Icon size={23} />
                  </div>

                  <div className="mt-6 text-2xl font-extrabold text-orange-500">
                    {level.amount}
                  </div>

                  <h3 className="mt-2 text-xl font-extrabold text-[#24103f]">
                    {level.title}
                  </h3>

                  <p className="mt-3 min-h-[84px] text-sm leading-6 text-slate-600">
                    {level.description}
                  </p>

                  <button
                    onClick={() => handleSupport(level.amount)}
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#24103f] px-4 py-3 font-bold text-white transition hover:bg-[#321556]"
                  >
                    Contribute {level.amount}
                    <ChevronRight size={17} />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mx-auto mt-10 max-w-3xl rounded-3xl border border-purple-100 bg-purple-50 p-7 text-center">
            <p className="font-semibold leading-7 text-[#24103f]">
              Want to contribute a different amount?
            </p>

            <p className="mt-2 text-sm leading-6 text-slate-600">
              Every contribution is valuable. Support the pilot with an amount
              that works for you.
            </p>

            <button
              onClick={() => handleSupport()}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3 font-bold text-white transition hover:bg-orange-600"
            >
              Choose my contribution
              <ArrowRight size={17} />
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================
          IMPACT / OUTCOMES
      ============================================================ */}
      <section id="impact" className="bg-slate-50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="font-bold uppercase tracking-[0.18em] text-orange-500">
                What success looks like
              </p>

              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#24103f] sm:text-4xl">
                We want your support to produce evidence — not just activity.
              </h2>

              <p className="mt-6 text-lg leading-8 text-slate-600">
                The pilot will help us understand whether MastaKash creates
                meaningful value for community financial groups and what is
                required to scale responsibly.
              </p>

              <div className="mt-8 flex items-start gap-4 rounded-2xl border border-purple-100 bg-white p-5">
                <div className="rounded-xl bg-purple-100 p-3 text-[#24103f]">
                  <BarChart3 size={23} />
                </div>

                <div>
                  <h3 className="font-extrabold text-[#24103f]">
                    Evidence for the next stage
                  </h3>

                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    Pilot results can inform product improvements, partnerships
                    and future funding conversations.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {pilotOutcomes.map((outcome, index) => (
                <div
                  key={outcome}
                  className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 font-bold text-orange-500">
                    {index + 1}
                  </div>

                  <span className="font-semibold text-slate-700">
                    {outcome}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          BUDGET
      ============================================================ */}
      <section id="budget" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="font-bold uppercase tracking-[0.18em] text-orange-500">
                Where the money goes
              </p>

              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#24103f] sm:text-4xl">
                A KES 25,000 launch fund with a clear job.
              </h2>

              <p className="mt-6 leading-8 text-slate-600">
                The initial fund is designed to finance the practical field
                activities required to get MastaKash in front of community
                financial groups and begin generating pilot evidence.
              </p>

              <div className="mt-8 rounded-3xl bg-[#24103f] p-7 text-white">
                <div className="text-sm font-semibold text-purple-200">
                  Total Pilot Launch Fund
                </div>

                <div className="mt-2 text-4xl font-extrabold">
                  KES {formatKES(goal)}
                </div>

                <div className="mt-5 h-px bg-white/10" />

                <div className="mt-5 flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 shrink-0 text-orange-400" />

                  <p className="text-sm leading-6 text-purple-100">
                    We intend to track how pilot funds are used and document
                    the activities and evidence they enable.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {budgetItems.map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-5"
                >
                  <div className="flex items-center justify-between gap-5">
                    <span className="font-semibold text-slate-700">
                      {item.label}
                    </span>

                    <span className="shrink-0 font-extrabold text-[#24103f]">
                      KES {formatKES(item.amount)}
                    </span>
                  </div>

                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-orange-500"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>

                  <div className="mt-2 text-xs font-semibold text-slate-400">
                    {item.percentage}% of launch fund
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          CAPITAL JOURNEY
      ============================================================ */}
      <section className="bg-purple-50 py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 text-center lg:px-8">
          <p className="font-bold uppercase tracking-[0.18em] text-orange-500">
            The bigger journey
          </p>

          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#24103f] sm:text-4xl">
            The pilot is a bridge from what we have built to what we can prove.
          </h2>

          <div className="mt-12 grid gap-3 md:grid-cols-5">
            {[
              ["01", "Pilot Fund", "KES 25K"],
              ["02", "Field Reach", "10–15 groups"],
              ["03", "Validation", "Real usage"],
              ["04", "Evidence", "Pilot results"],
              ["05", "Scale", "Next capital"],
            ].map(([number, title, text], index) => (
              <React.Fragment key={number}>
                <div className="rounded-2xl bg-white p-5 shadow-sm">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[#24103f] text-sm font-extrabold text-white">
                    {number}
                  </div>

                  <h3 className="mt-4 font-extrabold text-[#24103f]">
                    {title}
                  </h3>

                  <p className="mt-1 text-sm text-slate-500">{text}</p>
                </div>

                {index < 4 && (
                  <div className="hidden items-center justify-center md:flex">
                    <ArrowRight size={18} className="text-orange-500" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          TRANSPARENCY
      ============================================================ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100 text-[#24103f]">
            <ShieldCheck size={28} />
          </div>

          <h2 className="mt-6 text-3xl font-extrabold tracking-tight text-[#24103f]">
            Your support should create something measurable.
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-600">
            We will document the pilot journey and track what the initial fund
            enables — from groups reached and onboarded to user feedback,
            platform usage and lessons learned.
          </p>

          <div className="mt-8 grid gap-3 text-left sm:grid-cols-2">
            {[
              "Groups reached",
              "Groups onboarded",
              "User participation",
              "Platform usage",
              "User feedback",
              "Key lessons",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 rounded-xl bg-slate-50 p-4"
              >
                <CheckCircle2 size={18} className="text-orange-500" />
                <span className="font-semibold text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
      ============================================================ */}
      <section className="bg-[#24103f] py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-xl">
            <HandHeart size={30} />
          </div>

          <h2 className="mt-7 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            Help us take the next step.
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-purple-100">
            We have built MastaKash. Now we need to put it in the hands of the
            communities it was built for.
          </p>

          <div className="mx-auto mt-10 max-w-xl rounded-3xl bg-white p-6 text-left shadow-2xl sm:p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-500">
                  Pilot Launch Fund
                </p>
                <p className="mt-1 text-3xl font-extrabold text-[#24103f]">
                  KES {formatKES(goal)}
                </p>
              </div>

              <Target className="text-orange-500" size={30} />
            </div>

            <div className="mt-6 h-3 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-orange-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="mt-2 flex justify-between text-xs font-semibold text-slate-400">
              <span>KES {formatKES(raised)} raised</span>
              <span>KES {formatKES(goal)} goal</span>
            </div>

            <button
              onClick={() => handleSupport()}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-4 font-extrabold text-white transition hover:bg-orange-600"
            >
              Support the MastaKash Pilot
              <ArrowRight size={19} />
            </button>
          </div>

          <p className="mt-8 text-sm text-purple-300">
            60 days • 10–15 groups • Trans Nzoia & Kakamega
          </p>
        </div>
      </section>

      {/* ============================================================
          FOOTER
      ============================================================ */}
      <footer className="bg-[#180b2b] text-purple-200">
        <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-center">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500 text-white">
                  <Wallet size={18} />
                </div>

                <span className="font-bold text-white">MastaKash</span>
              </div>

              <p className="mt-3 max-w-md text-sm leading-6">
                Building digital infrastructure for community finance.
              </p>
            </div>

            <div className="flex flex-wrap gap-5 text-sm">
              <a
                href="#pilot"
                className="transition hover:text-white"
              >
                Pilot
              </a>

              <a
                href="#impact"
                className="transition hover:text-white"
              >
                Impact
              </a>

              <a
                href="#budget"
                className="transition hover:text-white"
              >
                Budget
              </a>

              <button
                onClick={() => handleSupport()}
                className="font-bold text-orange-400 transition hover:text-orange-300"
              >
                Support
              </button>
            </div>
          </div>

          <div className="mt-8 border-t border-white/10 pt-6 text-xs text-purple-400">
            © {new Date().getFullYear()} MastaKash. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}